import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, Plus, MessageSquare, ChevronRight, ChevronDown,
  Send, Loader2, ArrowLeft, Search, BookOpen, ThumbsUp, ThumbsDown,
  Ticket, Lightbulb, Sparkles, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'account', label: 'Account & Security' },
  { value: 'feature_request', label: 'Feature Request' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-slate-400 bg-slate-500/10 border-slate-500/20' },
  { value: 'medium', label: 'Medium', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { value: 'high', label: 'High', color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
];

const STATUS_STYLES = {
  open: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  in_progress: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  resolved: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  closed: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
};

const FAQ_CAT_LABELS = {
  getting_started: 'Getting Started',
  account: 'Account & Security',
  billing: 'Billing & Payments',
  lectures: 'Lectures & Recording',
  ai_features: 'AI Features',
  study_tools: 'Study Tools',
  technical: 'Technical Support',
  general: 'General',
};

const FAQ_CAT_ICONS = {
  getting_started: Lightbulb,
  account: HelpCircle,
  billing: HelpCircle,
  lectures: HelpCircle,
  ai_features: HelpCircle,
  study_tools: BookOpen,
  technical: HelpCircle,
  general: HelpCircle,
};

/* ─── FAQ Accordion Item ────────────────────────────── */
const FAQItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  const [voted, setVoted] = useState(null);

  const vote = async (helpful) => {
    if (voted !== null) return;
    try {
      await axios.post(`${API}/support/faq/${faq.id}/helpful?helpful=${helpful}`);
      setVoted(helpful);
    } catch (err) { console.error('FAQ vote failed:', err); }
  };

  return (
    <div
      className="rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl overflow-hidden transition-all"
      data-testid={`faq-item-${faq.id}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-white/[0.02] transition-colors"
        data-testid={`faq-toggle-${faq.id}`}
      >
        <span className="text-sm font-medium text-[var(--isp-heading)] leading-relaxed pr-2">
          {faq.question}
        </span>
        <ChevronDown className={`w-4 h-4 text-[var(--isp-muted)] flex-shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-3">
              <p className="text-sm text-[var(--isp-body)] leading-relaxed whitespace-pre-wrap">
                {faq.answer}
              </p>
              {faq.tags?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {faq.tags.map(t => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[var(--isp-muted)] border border-[var(--isp-glass-border)]">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 pt-1">
                <span className="text-xs text-[var(--isp-muted)]">Was this helpful?</span>
                <button
                  onClick={() => vote(true)}
                  disabled={voted !== null}
                  className={`p-1.5 rounded-lg transition-colors ${voted === true ? 'bg-emerald-500/20 text-emerald-400' : 'hover:bg-white/5 text-[var(--isp-muted)]'}`}
                  data-testid={`faq-helpful-yes-${faq.id}`}
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => vote(false)}
                  disabled={voted !== null}
                  className={`p-1.5 rounded-lg transition-colors ${voted === false ? 'bg-red-500/20 text-red-400' : 'hover:bg-white/5 text-[var(--isp-muted)]'}`}
                  data-testid={`faq-helpful-no-${faq.id}`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Main Support Page ─────────────────────────────── */
const SupportPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('faq'); // faq | tickets
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | detail
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });

  // Smart Suggestions state
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  const debounceRef = useRef(null);

  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqGrouped, setFaqGrouped] = useState({});
  const [faqSearch, setFaqSearch] = useState('');
  const [faqCategory, setFaqCategory] = useState('all');
  const [faqLoading, setFaqLoading] = useState(true);
  const searchRef = useRef(null);

  useEffect(() => { loadFAQs(); loadTickets(); }, []);

  // Debounced smart suggestion search when subject changes
  const searchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 3) {
      setSmartSuggestions([]);
      return;
    }
    setSuggestionsLoading(true);
    try {
      const res = await axios.get(`${API}/support/faq?search=${encodeURIComponent(query.trim())}`);
      const results = (res.data.faqs || []).slice(0, 4);
      setSmartSuggestions(results);
      // Track suggestions shown
      if (results.length > 0) {
        axios.post(`${API}/support/analytics/suggestion-shown`, results.map(f => f.id)).catch(() => {});
      }
    } catch {
      setSmartSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const handleSubjectChange = (value) => {
    setForm(f => ({ ...f, subject: value }));
    setSuggestionDismissed(false);
    setExpandedSuggestion(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSuggestions(value), 400);
  };

  const loadFAQs = async () => {
    setFaqLoading(true);
    try {
      const res = await axios.get(`${API}/support/faq`);
      setFaqs(res.data.faqs || []);
      setFaqGrouped(res.data.grouped || {});
    } catch (err) { console.error('Failed to load FAQs:', err); } finally {
      setFaqLoading(false);
    }
  };

  const loadTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`);
      setTickets(res.data.tickets || []);
    } catch (err) { console.error('Failed to load tickets:', err); } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, form);
      toast.success('Ticket submitted! We\'ll get back to you soon.');
      setTickets(prev => [res.data, ...prev]);
      setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      setView('list');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const openTicket = async (ticket) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setView('detail');
    } catch {
      toast.error('Failed to load ticket');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, { message: replyText });
      toast.success('Reply sent');
      setReplyText('');
      const res = await axios.get(`${API}/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  // Filtered FAQs
  const filteredFaqs = faqs.filter(f => {
    const matchCat = faqCategory === 'all' || f.category === faqCategory;
    if (!faqSearch.trim()) return matchCat;
    const term = faqSearch.toLowerCase();
    return matchCat && (
      f.question.toLowerCase().includes(term) ||
      f.answer.toLowerCase().includes(term) ||
      (f.tags || []).some(t => t.includes(term))
    );
  });

  // Group filtered FAQs
  const filteredGrouped = {};
  filteredFaqs.forEach(f => {
    const cat = f.category || 'general';
    if (!filteredGrouped[cat]) filteredGrouped[cat] = [];
    filteredGrouped[cat].push(f);
  });

  if (loading && faqLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="f500-page space-y-6 sm:space-y-8 max-w-4xl mx-auto" data-testid="support-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight">
          Help & Support
        </h1>
        <p className="text-[var(--isp-body)] mt-2 text-base">
          Find answers or contact our support team
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 p-1 rounded-xl bg-[var(--isp-glass)] backdrop-blur-xl border border-[var(--isp-glass-border)] w-fit">
        <button
          onClick={() => { setTab('faq'); setView('list'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'faq' ? 'bg-white/10 text-white' : 'text-[var(--isp-body)] hover:text-white hover:bg-white/5'
          }`}
          data-testid="tab-faq"
        >
          <BookOpen className="w-4 h-4" />
          Knowledge Base
        </button>
        <button
          onClick={() => setTab('tickets')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'tickets' ? 'bg-white/10 text-white' : 'text-[var(--isp-body)] hover:text-white hover:bg-white/5'
          }`}
          data-testid="tab-tickets"
        >
          <Ticket className="w-4 h-4" />
          My Tickets
          {tickets.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300 font-medium">{tickets.length}</span>
          )}
        </button>
      </div>

      {/* ═══ FAQ TAB ═══ */}
      {tab === 'faq' && (
        <div className="space-y-5">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--isp-muted)]" />
            <Input
              ref={searchRef}
              value={faqSearch}
              onChange={e => setFaqSearch(e.target.value)}
              placeholder="Search the knowledge base..."
              className="pl-11 h-12 rounded-xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder:text-[var(--isp-muted)]"
              data-testid="faq-search-input"
            />
          </div>

          {/* Category Filters */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFaqCategory('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                faqCategory === 'all' ? 'bg-white/10 text-white border-white/20' : 'text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-white/5'
              }`}
              data-testid="faq-cat-all"
            >
              All
            </button>
            {Object.keys(faqGrouped).map(cat => (
              <button
                key={cat}
                onClick={() => setFaqCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  faqCategory === cat ? 'bg-white/10 text-white border-white/20' : 'text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-white/5'
                }`}
                data-testid={`faq-cat-${cat}`}
              >
                {FAQ_CAT_LABELS[cat] || cat}
              </button>
            ))}
          </div>

          {/* FAQ Content */}
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-16  border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl">
              <Search className="w-12 h-12 mx-auto mb-3 text-[var(--isp-muted)]" />
              <h3 className="text-lg font-medium text-[var(--isp-heading)] mb-2">No results found</h3>
              <p className="text-sm text-[var(--isp-body)] mb-5">
                {faqSearch ? `No articles match "${faqSearch}"` : 'No FAQ entries yet.'}
              </p>
              <Button
                onClick={() => { setTab('tickets'); setView('create'); }}
                className="rounded-xl gap-2 bg-violet-600 hover:bg-violet-500 text-white"
                data-testid="faq-create-ticket-btn"
              >
                <MessageSquare className="w-4 h-4" />
                Submit a Ticket Instead
              </Button>
            </div>
          ) : faqCategory === 'all' && !faqSearch ? (
            // Grouped display
            Object.entries(filteredGrouped).map(([cat, items]) => (
              <div key={cat} className="space-y-2">
                <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2 px-1 pt-2">
                  <BookOpen className="w-4 h-4 text-violet-400" />
                  {FAQ_CAT_LABELS[cat] || cat}
                  <span className="text-[10px] text-[var(--isp-muted)] font-normal">({items.length})</span>
                </h3>
                <div className="space-y-2">
                  {items.map(faq => <FAQItem key={faq.id} faq={faq} />)}
                </div>
              </div>
            ))
          ) : (
            // Flat display for filtered/search
            <div className="space-y-2">
              {filteredFaqs.map(faq => <FAQItem key={faq.id} faq={faq} />)}
            </div>
          )}

          {/* CTA */}
          {filteredFaqs.length > 0 && (
            <div className="text-center pt-4 pb-2">
              <p className="text-sm text-[var(--isp-body)] mb-3">Can't find what you're looking for?</p>
              <Button
                onClick={() => { setTab('tickets'); setView('create'); }}
                variant="outline"
                className="rounded-xl gap-2 border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
                data-testid="faq-submit-ticket-cta"
              >
                <MessageSquare className="w-4 h-4" />
                Submit a Support Ticket
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ═══ TICKETS TAB ═══ */}
      {tab === 'tickets' && (
        <div className="space-y-4">
          {/* Ticket Header */}
          <div className="flex justify-between items-center">
            {view === 'list' && (
              <Button
                onClick={() => setView('create')}
                className="gap-2 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white rounded-xl"
                data-testid="new-ticket-btn"
              >
                <Plus className="w-4 h-4" />
                New Ticket
              </Button>
            )}
            {view !== 'list' && (
              <Button
                variant="outline"
                onClick={() => { setView('list'); setSelectedTicket(null); }}
                className="gap-2 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]"
                data-testid="back-to-tickets-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Tickets
              </Button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {/* Create Ticket */}
            {view === 'create' && (
              <motion.div
                key="create"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className=" border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl p-6"
                data-testid="create-ticket-form"
              >
                <h2 className="text-lg font-semibold text-[var(--isp-heading)] mb-5 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-violet-400" />
                  Submit a Support Ticket
                </h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject *</Label>
                    <Input
                      value={form.subject}
                      onChange={e => handleSubjectChange(e.target.value)}
                      placeholder="Brief summary of your issue"
                      className="rounded-xl"
                      maxLength={200}
                      data-testid="ticket-subject-input"
                    />
                  </div>

                  {/* Smart Suggestions */}
                  <AnimatePresence>
                    {!suggestionDismissed && (smartSuggestions.length > 0 || suggestionsLoading) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                        data-testid="smart-suggestions-panel"
                      >
                        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-amber-400" />
                              <span className="text-sm font-medium text-amber-300">
                                {suggestionsLoading ? 'Searching knowledge base...' : `${smartSuggestions.length} article${smartSuggestions.length !== 1 ? 's' : ''} might help`}
                              </span>
                            </div>
                            <button
                              onClick={() => setSuggestionDismissed(true)}
                              className="text-[10px] px-2 py-1 rounded-lg text-[var(--isp-muted)] hover:text-white hover:bg-white/5 transition-colors"
                              data-testid="dismiss-suggestions-btn"
                            >
                              Dismiss
                            </button>
                          </div>

                          {suggestionsLoading ? (
                            <div className="flex items-center gap-2 py-2">
                              <Loader2 className="w-4 h-4 animate-spin text-amber-400/60" />
                              <span className="text-xs text-[var(--isp-muted)]">Looking for answers...</span>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {smartSuggestions.map(faq => (
                                <div
                                  key={faq.id}
                                  className="rounded-lg border border-[var(--isp-glass-border)] bg-white/[0.02] overflow-hidden"
                                  data-testid={`suggestion-${faq.id}`}
                                >
                                  <button
                                    onClick={() => setExpandedSuggestion(expandedSuggestion === faq.id ? null : faq.id)}
                                    className="w-full text-left px-4 py-3 flex items-start justify-between gap-2 hover:bg-white/[0.02] transition-colors"
                                    data-testid={`suggestion-toggle-${faq.id}`}
                                  >
                                    <span className="text-sm text-[var(--isp-heading)] leading-relaxed">{faq.question}</span>
                                    <ChevronDown className={`w-3.5 h-3.5 text-[var(--isp-muted)] flex-shrink-0 mt-1 transition-transform duration-200 ${expandedSuggestion === faq.id ? 'rotate-180' : ''}`} />
                                  </button>
                                  <AnimatePresence>
                                    {expandedSuggestion === faq.id && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                      >
                                        <div className="px-4 pb-3 space-y-3">
                                          <p className="text-sm text-[var(--isp-body)] leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              // Track deflection
                                              axios.post(`${API}/support/analytics/deflection`, null, {
                                                params: { faq_id: faq.id, query: form.subject }
                                              }).catch(() => {});
                                              toast.success('Glad we could help!');
                                              setSmartSuggestions([]);
                                              setSuggestionDismissed(true);
                                              setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
                                              setView('list');
                                            }}
                                            className="gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
                                            data-testid={`suggestion-resolved-${faq.id}`}
                                          >
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            This resolved my issue
                                          </Button>
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Describe your issue in detail..."
                      rows={5}
                      className="rounded-xl resize-none"
                      maxLength={5000}
                      data-testid="ticket-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <select
                        value={form.category}
                        onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                        className="w-full rounded-xl bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-heading)] outline-none"
                        data-testid="ticket-category-select"
                      >
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <select
                        value={form.priority}
                        onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                        className="w-full rounded-xl bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-heading)] outline-none"
                        data-testid="ticket-priority-select"
                      >
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <Button
                    onClick={createTicket}
                    disabled={creating || !form.subject.trim() || !form.description.trim()}
                    className="w-full rounded-xl gap-2 bg-violet-600 hover:bg-violet-500 text-white h-11"
                    data-testid="submit-ticket-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {creating ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Ticket List */}
            {view === 'list' && (
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {tickets.length === 0 ? (
                  <div className="text-center py-16  border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl">
                    <Ticket className="w-16 h-16 mx-auto mb-4 text-[var(--isp-muted)]" />
                    <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No tickets yet</h3>
                    <p className="text-[var(--isp-body)] mb-6">Check the Knowledge Base first, or submit a ticket if you need help</p>
                    <Button
                      onClick={() => setView('create')}
                      className="rounded-xl gap-2 bg-violet-600 hover:bg-violet-500 text-white"
                    >
                      <Plus className="w-4 h-4" />
                      New Ticket
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tickets.map(t => (
                      <button
                        key={t.id}
                        onClick={() => openTicket(t)}
                        className="w-full text-left p-4  border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl hover:bg-[var(--isp-glass-hover)] transition-all group"
                        data-testid={`ticket-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[t.status] || STATUS_STYLES.open}`}>
                                {t.status.replace('_', ' ')}
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${PRIORITIES.find(p => p.value === t.priority)?.color || ''}`}>
                                {t.priority}
                              </span>
                              <span className="text-[10px] text-[var(--isp-muted)]">
                                {CATEGORIES.find(c => c.value === t.category)?.label}
                              </span>
                            </div>
                            <h3 className="text-sm font-medium text-[var(--isp-heading)] truncate">{t.subject}</h3>
                            <p className="text-xs text-[var(--isp-body)] mt-0.5">
                              {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''} · {t.replies?.length || 0} replies
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[var(--isp-muted)] group-hover:text-[var(--isp-heading)] transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Ticket Detail */}
            {view === 'detail' && selectedTicket && (
              <motion.div key="detail" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className=" border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl p-6">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium ${STATUS_STYLES[selectedTicket.status]}`}>
                          {selectedTicket.status.replace('_', ' ')}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium ${PRIORITIES.find(p => p.value === selectedTicket.priority)?.color}`}>
                          {selectedTicket.priority}
                        </span>
                      </div>
                      <h2 className="text-lg font-semibold text-[var(--isp-heading)]">{selectedTicket.subject}</h2>
                      <p className="text-xs text-[var(--isp-body)] mt-1">
                        #{selectedTicket.id} · {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-[var(--isp-glass-border)] text-sm text-[var(--isp-body)] whitespace-pre-wrap">
                    {selectedTicket.description}
                  </div>
                </div>

                {selectedTicket.replies?.length > 0 && (
                  <div className="space-y-3">
                    {selectedTicket.replies.map((r, i) => (
                      <div
                        key={r.id || i}
                        className={` border p-4 ${
                          r.is_admin
                            ? 'border-violet-500/20 bg-violet-500/[0.04]'
                            : 'border-[var(--isp-glass-border)] bg-[var(--isp-glass)]'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-[var(--isp-heading)]">
                            {r.is_admin ? 'Support Team' : r.user_name || 'You'}
                          </span>
                          {r.is_admin && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 font-medium">Admin</span>
                          )}
                          <span className="text-xs text-[var(--isp-muted)] ml-auto">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-sm text-[var(--isp-body)] whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {selectedTicket.status !== 'closed' && (
                  <div className=" border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl p-4">
                    <Textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows={3}
                      className="rounded-xl resize-none mb-3"
                      data-testid="ticket-reply-input"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      className="rounded-xl gap-2 bg-violet-600 hover:bg-violet-500 text-white"
                      data-testid="send-reply-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send Reply
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
